import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const AllTeam = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [teamData, setTeamData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [availableLevels, setAvailableLevels] = useState([]);

  // Fetch all team members from API
  const fetchTeam = async () => {
    const UserId = localStorage.getItem("UserId");

    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API_BASE}/allteam`, { UserId });

      if (response.data.success && response.data.userd && Array.isArray(response.data.userd)) {
        const mappedData = response.data.userd.map((user) => ({
          Userid: user.Userid,
          Name: user.Namec || user.Name || '-',
          Mobile: user.Mobile || '-',
          email: user.email || '-',
          Sponserid: user.Sponserid || '-',
          RankName: user.RankName || 'Starter',
          dateofjoin: user.dateofjoin,
          ulevel: user.ulevel || 0,
          packageamount: user.packageamount || 0,
          commission: user.commission || 0,
          Statusid: user.Statusid
        }));
        
        setTeamData(mappedData);
        
        // Get unique levels for filter
        const uniqueLevels = [...new Set(mappedData.map(m => m.ulevel))].filter(l => l > 0).sort((a, b) => a - b);
        setAvailableLevels(uniqueLevels);
      } else {
        setTeamData([]);
        setError(response.data.error || "No data available");
      }
    } catch (err) {
      console.error(err);
      setTeamData([]);
      setError(err.response?.data?.error || "Failed to fetch data");
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  // Filter by search, level and status
  const filteredMembers = teamData.filter(member => {
    const matchesSearch = member.Userid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          member.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          member.Mobile?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLevel = filterLevel === 'all' || member.ulevel === parseInt(filterLevel);
    
    let matchesStatus = true;
    if (filterStatus !== 'all') {
      const isActive = member.Statusid === 1;
      if (filterStatus === 'active') matchesStatus = isActive;
      else if (filterStatus === 'inactive') matchesStatus = !isActive;
    }
    return matchesSearch && matchesLevel && matchesStatus;
  });

  // Pagination
  const paginatedMembers = filteredMembers.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const totalPages = Math.ceil(filteredMembers.length / entriesPerPage);

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    const isActive = status === 1;
    let label = "Inactive";
    let color = "#ef4444";
    
    if (isActive) {
      label = "Active";
      color = "#10b981";
    }

    return (
      <span className={`status-badge ${label.toLowerCase()}`} style={{ backgroundColor: color }}>
        {label}
      </span>
    );
  };

  // Handle entries per page change
  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['User ID', 'Name', 'Mobile', 'Email', 'Level', 'Sponsor', 'Join Date', 'Investment', 'Commission', 'Status'];
    const csvData = filteredMembers.map(member => [
      member.Userid,
      member.Name,
      member.Mobile,
      member.email,
      member.ulevel,
      member.Sponserid,
      member.dateofjoin ? new Date(member.dateofjoin).toLocaleDateString() : '-',
      `$${member.packageamount || 0}`,
      `$${member.commission || 0}`,
      member.Statusid === 1 ? 'Active' : 'Inactive'
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all_team_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported successfully!");
  };

  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    if (start > 1) {
      buttons.push(
        <button key="first" onClick={() => setCurrentPage(1)} className="page-btn">
          1
        </button>
      );
      if (start > 2) buttons.push(<span key="dots1" className="page-dots">...</span>);
    }
    
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`page-btn ${currentPage === i ? 'active-page' : ''}`}
        >
          {i}
        </button>
      );
    }
    
    if (end < totalPages) {
      if (end < totalPages - 1) buttons.push(<span key="dots2" className="page-dots">...</span>);
      buttons.push(
        <button key="last" onClick={() => setCurrentPage(totalPages)} className="page-btn">
          {totalPages}
        </button>
      );
    }
    
    return buttons;
  };

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="team-page">
        <div className="page-header">
          <div>
            <h1>All Team Members</h1>
            <p>View your entire network and team structure</p>
          </div>
          {/* <button className="btn-primary" onClick={exportToCSV}>
            <i className="fa fa-download"></i> Export Report
          </button> */}
        </div>

        {/* DataTable Controls */}
        <div className="datatable-controls">
          <div className="datatable-length">
            <label>Show</label>
            <select value={entriesPerPage} onChange={handleEntriesChange}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <label>entries</label>
          </div>
          
          <div className="datatable-search">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search by User ID, Name, Email or Mobile..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {/* Filter Bar */}
        <div className="filter-bar">
          <div className="filter-group" style={{marginRight : '10px;'}}>
            <label>Filter by Level:</label>
            <select value={filterLevel} onChange={(e) => {
              setFilterLevel(e.target.value);
              setCurrentPage(1);
            }}>
              <option value="all">All Levels</option>
              {availableLevels.map(level => (
                <option key={level} value={level}>Level {level}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Filter by Status:</label>
            <select value={filterStatus} onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* DataTable - Desktop View with Horizontal Scroll for Mobile */}
        <div className="datatable-container">
          <div className="table-wrapper">
            <table className="datatable">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User ID</th>
                  <th>Name</th>
                  <th>Level</th>
                  <th>Sponsor</th>
                  <th>Rank</th>
                  <th>Join Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className="loading-row">
                    <td colSpan="12">
                      <div className="loader"></div> Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr className="error-row">
                    <td colSpan="12">
                      <i className="fa fa-exclamation-circle"></i> {error}
                    </td>
                  </tr>
                ) : paginatedMembers.length === 0 ? (
                  <tr className="empty-row">
                    <td colSpan="12">
                      <i className="fa fa-users"></i> No team members found
                    </td>
                  </tr>
                ) : (
                  paginatedMembers.map((member, index) => (
                    <tr key={member.Userid || index}>
                      <td>{((currentPage - 1) * entriesPerPage) + index + 1}</td>
                      <td><span className="user-id">{member.Userid}</span></td>
                      <td>{member.Name}</td>
                      <td><span className="level-badge">Level {member.ulevel}</span></td>
                      <td>{member.Sponserid}</td>
                      <td>{member.RankName}</td>
                      <td>{member.dateofjoin ? new Date(member.dateofjoin).toLocaleDateString() : '-'}</td>
                      <td><StatusBadge status={member.Statusid} /></td>
                      
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && !loading && !error && filteredMembers.length > 0 && (
            <div className="datatable-footer">
              <div className="datatable-info">
                Showing {((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, filteredMembers.length)} of {filteredMembers.length} entries
              </div>
              <div className="datatable-pagination">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                  disabled={currentPage === 1}
                  className="page-nav"
                >
                  <i className="fa fa-chevron-left"></i>
                </button>
                {getPaginationButtons()}
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                  disabled={currentPage === totalPages}
                  className="page-nav"
                >
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AllTeam;